'use client';
// Plan d'entretien et de maintenance préventive interne — ISO 15189 §6.4.4.
//
// Cet écran remplace un classeur : « ENR.J1.01 Planning d'entretien et de
// maintenance préventive interne ». Le document existait, il était juste, et il
// ne servait à rien — personne ne peut se souvenir tous les matins que l'Indiko
// veut sa maintenance à 7 h, que le STA Compact veut la sienne le samedi à 13 h
// et que le Cobas veut la sienne le 15 ET le 30.
//
// ── Ce que l'écran refuse de faire ─────────────────────────────────────────
//
//  · Il ne PROPOSE aucune fréquence par défaut. Le registre part vide, et il le
//    dit. Une périodicité inventée par l'outil serait ensuite défendue devant un
//    auditeur comme si le laboratoire l'avait choisie.
//  · Il n'affiche jamais « en retard » quand le délai de tolérance n'est pas
//    paramétré : il affiche « échue » et dit que le délai manque. Qualifier un
//    retard sans règle, c'est fabriquer un constat.
//  · Il ne marque rien fait tout seul. Une échéance non acquittée reste là.
//
// Le même composant sert l'écran dédié et l'onglet « Maintenance » du dossier
// d'un équipement (`<Maintenances equipement="EQ01" />`) — comme Calibrations.
// Deux écrans finiraient par ne plus dire les mêmes dates.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BoutonJournal } from '@/components/BoutonJournal';
import { useGed } from '@/lib/store';
import { fmtDate, aujourdhuiIso } from '@/lib/meta';
import { useRemonteEnHaut, I, Modale, card, inputStyle, EmptyState, FilterBar, RefISO, Selecteur, ConfirmButton, useBusy, Pastille, usePagination, Pagination } from '@/components/ui';
import { EnteteEcran } from '@/components/EnteteEcran';
import { OngletsDossier, PanneauDossier } from '@/components/Dossier';
import { useOngletAdresse } from '@/lib/useAdresse';
import { ligneOuvrable, SansOuvrir, Tableau } from '@/shared/Tableau';
import { H1, lbl } from './commun';
import type {
  MaintenanceRegistre, MaintenanceLigne, MaintenanceOccurrence,
  PropositionsRegistre, Proposition, PropositionLigne, PropositionDecision,
} from '@/lib/types';

import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import { peutGererParc } from '@/lib/droits-parc';
import { MaintenancesHistorique } from './MaintenancesHistorique';
import { SaisirMaintenance } from './SaisirMaintenance';
import { TYPES_DOCUMENT } from '@/lib/documents-maintenance';
import { VideoDemonstration } from '@/components/VideoDemonstration';
type Vue = 'echeances' | 'plan' | 'historique' | 'propositions' | 'parametrage';
/* La liste des valeurs ADMISES dans l'adresse. Elle existe pour refuser
   `?onglet=nimportequoi` : sans elle, une adresse tronquée ou bricolée
   ouvrirait un onglet vide au lieu du premier. */
const VUES: readonly Vue[] = ['echeances', 'plan', 'historique', 'propositions', 'parametrage'];

const JOURS = [
  { n: 1, court: 'Lun' }, { n: 2, court: 'Mar' }, { n: 3, court: 'Mer' }, { n: 4, court: 'Jeu' },
  { n: 5, court: 'Ven' }, { n: 6, court: 'Sam' }, { n: 7, court: 'Dim' },
];
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

/**
 * Couleur et libellé d'un état d'échéance.
 *
 * « Due » et « en retard » sont DEUX états, et deux couleurs. Les confondre est
 * exactement le défaut que ce lot vient éviter : une maintenance due
 * aujourd'hui n'est pas un manquement, c'est le travail du jour.
 */
const ETATS: Record<string, { label: string; fg: string; bg: string }> = {
  a_venir: { label: 'à venir', fg: 'var(--text-muted)', bg: 'var(--surface-sunken)' },
  due: { label: 'à faire', fg: 'var(--color-primary-active)', bg: 'var(--cyan-50)' },
  toleree: { label: 'échue — dans le délai', fg: 'var(--amber-600)', bg: 'var(--amber-50)' },
  en_retard: { label: 'en retard', fg: 'var(--red-600)', bg: 'var(--red-50)' },
  faite: { label: 'faite', fg: 'var(--green-600)', bg: 'var(--green-50)' },
  faite_tard: { label: 'faite hors délai', fg: 'var(--amber-600)', bg: 'var(--amber-50)' },
};

/* Exportée (05/10/2026) : le mémo du jour affiche les mêmes états à côté de la
   check-list. Une seconde table de couleurs aurait fini par peindre « due » et
   « en retard » de la même teinte à un écran d'écart — or c'est très exactement
   la distinction que ce lot existe pour tenir. */
export function EtatPill({ o }: { o: MaintenanceOccurrence }) {
  const m = ETATS[o.etat] || ETATS.due;
  /* L9 : sur la Pastille du socle (É-12) ; la couleur vient de l'ÉTAT (`ETATS`), partagé avec le mémo du jour. */
  return <Pastille bg={m.bg} fg={m.fg} compacte title={o.motifEtat}>{m.label}</Pastille>;
}

/* ═══════════════════════════════════════════════════ acquittement d'un acte */

/**
 * Déclarer une échéance faite.
 *
 * Le formulaire fait déclarer les opérations UNE PAR UNE, faite ou non faite,
 * parce qu'une opération non faite n'est pas une case oubliée : l'acte la
 * conserve telle quelle, avec son motif (28/10/2026). Cocher tout d'un bouton existerait sinon comme la
 * seule chose qu'on ferait jamais.
 */
export function Acquitter({ occ, ligne, operationsConnues, onFerme, onFait }: {
  occ: MaintenanceOccurrence;
  ligne: MaintenanceLigne | undefined;
  /** Les opérations, quand l'appelant les tient d'ailleurs que du registre.
   *
   *  ⚠️ Le mémo du jour (`MonJour`) est dans ce cas : il reçoit ses occurrences
   *  de `/ged/rh/mon-jour`, qui les sert à TOUT LE MONDE, là où le registre des
   *  lignes de plan est derrière la section « maintenances ». Sans cette porte,
   *  un technicien dont le paramétrage ferme cette section verrait un
   *  formulaire SANS AUCUNE OPÉRATION et les déclarerait toutes non faites sans
   *  qu'on les lui ait montrées. */
  operationsConnues?: string[];
  onFerme: () => void;
  onFait: () => void;
}) {
  const { smqData, joindreDocumentActe, equips } = useGed();
  const { busy, run } = useBusy();
  const operations = ligne?.operations || operationsConnues || [];
  /* Les vidéos de démonstration rangées au dossier de l'appareil, montrées là
     où le geste se fait (Ines, 03/11/2026). */
  const videos = (equips.find((x) => x.code === occ.equipementCode)?.pieces || [])
    .filter((p) => p.nature === 'video' && p.url);
  /* LES DOCUMENTS AVEC L'ACQUITTEMENT (29/10/2026) : rapport, bon, facture se
     joignaient seulement après coup, depuis l'historique. Choisis ici, chacun
     avec son type, ils partent une fois l'acte créé. */
  const [fichiers, setFichiers] = useState<File[]>([]);
  const [typesDoc, setTypesDoc] = useState<string[]>([]);
  const entreeDoc = useRef<HTMLInputElement>(null);
  /* ⚠️ TROIS ÉTATS PAR OPÉRATION (Inès, 28/10/2026) : faite, non faite, ou
     pas encore dite. Une case simplement laissée vide valait « non faite »
     sans que personne l'ait affirmé ; chaque opération se DÉCLARE maintenant,
     et une opération non faite porte son motif — exigé aussi par le moteur. */
  const [etats, setEtats] = useState<Record<string, 'fait' | 'non'>>({});
  const [motifs, setMotifs] = useState<Record<string, string>>({});
  const cochees = operations.filter((o) => etats[o] === 'fait');
  /* ⚠️ UNE ÉCHÉANCE PASSÉE PART SANS DATE (27/10/2026). Préremplie avec le
     jour de la saisie, elle a fait écrire le 19/10 que 113 échéances du 02/09
     au 19/10 avaient toutes été faites le même après-midi. Préremplie avec la
     date d'échéance, elle affirmerait à la place de l'opérateur que le geste a
     eu lieu au jour prévu. Seul celui qui l'a fait sait quand. */
  const passee = occ.date < aujourdhuiIso();
  const [dateFaite, setDateFaite] = useState(() => (passee ? '' : aujourdhuiIso()));
  const [commentaire, setCommentaire] = useState('');
  const [anomalie, setAnomalie] = useState(false);
  /* Échap passe par la Modale, qui demande avant de perdre les cases cochées :
     un `useEscape` posé ici fermait sans rien demander. */

  const nonDites = operations.filter((o) => !etats[o]).length;
  const nonFaites = operations.filter((o) => etats[o] === 'non');
  const sansMotif = nonFaites.filter((o) => !(motifs[o] || '').trim()).length;
  const pose = (o: string, v: 'fait' | 'non') => setEtats((e) => ({ ...e, [o]: v }));
  const bloque = !dateFaite ? 'Indiquez la date de réalisation.'
    : nonDites ? `${nonDites} opération(s) à déclarer faite ou non faite.`
      : sansMotif ? `${sansMotif} opération(s) non faite(s) sans motif.`
        : fichiers.some((_, i) => !typesDoc[i]) ? 'Dites le type de chaque document joint.' : '';

  const envoyer = () => run('ack', async () => {
    const r = await smqData<{ documentId?: string }>(`ged/maintenances/${occ.planId}/acquitter`, {
      datePrevue: occ.date, dateFaite, operations: cochees, commentaire, anomalie,
      motifs: Object.fromEntries(nonFaites.map((o) => [o, (motifs[o] || '').trim()])),
    }, 'Maintenance acquittée.');
    if (!r) return;
    // L'acte existe : un document refusé ne le défait pas, le magasin dit lequel (toast).
    if (r.documentId) for (const [i, f] of fichiers.entries()) await joindreDocumentActe(r.documentId, f, typesDoc[i]);
    onFait(); onFerme();
  });

  return (
    // Socle UX (11/08/2026) — Modale COMMUNE : Échap et clic sur le fond
    // ferment, corps seul défilant. L'ancien overlay artisanal ne se fermait
    // que par ses boutons, constaté en recette navigateur.
    <Modale titre={occ.libelle} largeur={640} onClose={onFerme}
      sousTitre={`${occ.maintenanceRef} · ${occ.equipementCode} — ${occ.equipementNom} · ${occ.frequenceLibelle}${occ.heure ? ` · ${occ.heure}` : ''}`}
      pied={(
        <>
          <Bouton taille="sm" onClick={onFerme}>Annuler</Bouton>
          <Bouton variante="primaire" taille="sm" onClick={envoyer} disabled={busy === 'ack' || !!bloque} title={bloque || undefined}>
            <I n="check" s={13} />Déclarer faite
          </Bouton>
        </>
      )}>
      <div style={{ display: 'grid', gap: 14 }}>

        <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--surface-sunken)', fontSize: 12.5, display: 'grid', gap: 4 }}>
          <div>Échéance acquittée : <strong style={{ fontFamily: 'var(--font-mono)' }}>{fmtDate(occ.date)}</strong></div>
          {/* Quand l'échéance a été déplacée, on le DIT ici : c'est le moment où
              quelqu'un peut contester le report, pas trois mois plus tard. */}
          {occ.deplacee && <div style={{ color: 'var(--text-muted)' }}>{occ.motifReport}</div>}
          <div style={{ color: 'var(--text-muted)' }}>{occ.motifEtat}</div>
        </div>

        <div>
          <label style={lbl} htmlFor="mnt-datefaite">Réalisée le</label>
          <input id="mnt-datefaite" type="date" value={dateFaite} max={aujourdhuiIso()}
            onChange={(e) => setDateFaite(e.target.value)} style={{ ...inputStyle, maxWidth: 200 }} />
          {passee && (
            <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
              Échéance du {fmtDate(occ.date)} : indiquez le jour du geste, pas celui de la saisie.
            </p>
          )}
        </div>

        {videos.length > 0 && (
          <div style={{ display: 'grid', gap: 6 }}>
            {videos.map((v) => <VideoDemonstration key={v.id} url={v.url!} libelle={v.libelle} />)}
          </div>
        )}

        {operations.length > 0 && (
          <div>
            <span style={lbl}>Opérations ({cochees.length}/{operations.length} faites{nonFaites.length ? `, ${nonFaites.length} non faite(s)` : ''})</span>
            {/* Lot 5 (11/08/2026) — la routine coche tout : une maintenance
                journalière faite en entier se déclarait en huit clics. Le
                geste inverse (tout effacer) existe pour repartir de zéro. */}
            <div style={{ display: 'flex', gap: 8, margin: '2px 0 6px' }}>
              <Bouton taille="sm" type="button" onClick={() => setEtats(Object.fromEntries(operations.map((o) => [o, 'fait'])))}>
                Tout déclarer fait
              </Bouton>
              {Object.keys(etats).length > 0 && (
                <Bouton taille="sm" type="button" onClick={() => setEtats({})}>
                  Tout effacer
                </Bouton>
              )}
            </div>
            <ol style={{ display: 'grid', gap: 6, margin: 0, paddingInlineStart: 22 }}>
              {operations.map((o) => (
                <li key={o} style={{ fontSize: 12.5 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <span style={{ flex: '1 1 260px', color: etats[o] === 'non' ? 'var(--red-600)' : 'inherit' }}>{o}</span>
                    <span role="radiogroup" aria-label={o} style={{ display: 'flex', gap: 12, whiteSpace: 'nowrap' }}>
                      <label style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
                        <input type="radio" name={`op-${o}`} checked={etats[o] === 'fait'} onChange={() => pose(o, 'fait')} />faite
                      </label>
                      <label style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
                        <input type="radio" name={`op-${o}`} checked={etats[o] === 'non'} onChange={() => pose(o, 'non')} />non faite
                      </label>
                    </span>
                  </div>
                  {etats[o] === 'non' && (
                    <input value={motifs[o] || ''} onChange={(e) => setMotifs((m) => ({ ...m, [o]: e.target.value }))}
                      aria-label={`Pourquoi « ${o} » n’a pas été faite`} maxLength={500} autoFocus
                      placeholder="Pourquoi n’a-t-elle pas été faite ? (obligatoire)"
                      style={{ ...inputStyle, marginTop: 4, borderColor: (motifs[o] || '').trim() ? undefined : 'var(--amber-600)' }} />
                  )}
                </li>
              ))}
            </ol>
            {bloque && (
              <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--amber-600)' }}>
                {bloque} Une opération non faite est enregistrée telle quelle, avec son motif — c’est ce qu’un évaluateur viendra lire.
              </p>
            )}
          </div>
        )}

        <div>
          <label style={lbl} htmlFor="mnt-comm">Commentaire</label>
          <textarea id="mnt-comm" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} rows={3}
            style={{ ...inputStyle, resize: 'vertical' }} placeholder="Ce qui a été constaté, ce qui n’a pas pu être fait…" />
        </div>

        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, cursor: 'pointer' }}>
          <input type="checkbox" checked={anomalie} onChange={(e) => setAnomalie(e.target.checked)} style={{ marginTop: 2 }} />
          <span>
            Cette maintenance a révélé une anomalie.
            <span style={{ color: 'var(--text-muted)' }}> La non-conformité se déclare ensuite au registre des NC (source « équipement ») : cette case la rend seulement visible depuis le plan.</span>
          </span>
        </label>

        <div style={{ display: 'grid', gap: 6 }}>
          <span style={lbl}>Documents (rapport, bon d’intervention, facture…)</span>
          {fichiers.map((f, i) => (
            <span key={`${f.name}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, flexWrap: 'wrap' }}>
              <I n="doc" s={13} />{f.name}
              <select aria-label={`Type du document ${f.name}`} value={typesDoc[i] || ''}
                onChange={(e) => setTypesDoc((l) => l.map((t, j) => (j === i ? e.target.value : t)))}
                style={{ ...inputStyle, width: 'auto', minWidth: 200, fontSize: 12.5, padding: '4px 8px', ...(typesDoc[i] ? {} : { color: 'var(--amber-600)' }) }}>
                <option value="">— type du document —</option>
                {TYPES_DOCUMENT.map((t) => <option key={t.k} value={t.k}>{t.label}</option>)}
              </select>
              <Bouton taille="sm" variante="discret" aria-label={`Retirer ${f.name}`} title="Retirer ce fichier (rien n’est encore envoyé)"
                onClick={() => { setFichiers((l) => l.filter((_, j) => j !== i)); setTypesDoc((l) => l.filter((_, j) => j !== i)); }}>
                <I n="x" s={12} />
              </Bouton>
            </span>
          ))}
          <input ref={entreeDoc} type="file" accept=".pdf,.jpg,.jpeg,.png" multiple style={{ display: 'none' }}
            onChange={(e) => {
              const liste = Array.from(e.target.files || []);
              if (liste.length) { setFichiers((a) => [...a, ...liste]); setTypesDoc((a) => [...a, ...liste.map(() => '')]); }
              e.target.value = '';
            }} />
          <div>
            <Bouton taille="sm" type="button" onClick={() => entreeDoc.current?.click()}>
              <I n="plus" s={13} />{fichiers.length ? 'Ajouter un fichier' : 'Joindre des documents'}
            </Bouton>
          </div>
        </div>

      </div>
    </Modale>
  );
}

/* ══════════════════════════════════════════════════ formulaire d'une ligne */

const LIGNE_VIDE = {
  equipementCode: '', libelle: '', nature: 'maintenance', paillasse: '',
  frequence: '', joursSemaine: [] as number[], joursMois: [] as number[], moisAncre: 1,
  heure: '', moment: '', operations: [] as string[], responsableKey: '', posteResponsable: '', politiqueReport: '',
  debutLe: '', finLe: '', sourceDocument: '', observation: '', actif: true,
};
type Brouillon = typeof LIGNE_VIDE;

export function brouillonDe(l: MaintenanceLigne): Brouillon {
  return {
    equipementCode: l.equipementCode, libelle: l.libelle, nature: l.nature, paillasse: l.paillasse,
    frequence: l.frequence,
    joursSemaine: l.ancrage?.joursSemaine || [],
    joursMois: l.ancrage?.joursMois || [],
    moisAncre: l.ancrage?.moisAncre || 1,
    heure: l.ancrage?.heure || '',
    moment: l.ancrage?.moment || '',
    operations: [...(l.operations || [])],
    responsableKey: l.responsableKey,
    posteResponsable: l.posteResponsable || '',
    politiqueReport: l.politiqueReport || '',
    debutLe: l.debutLe || '', finLe: l.finLe || '',
    sourceDocument: l.sourceDocument, observation: l.observation, actif: l.actif,
  };
}

/** Une opération par ligne, nettoyée de ce qu'apporte un tableau Word collé
 *  (28/10/2026) : « 1⇥Dépoussiérage extérieur⇥☐⇥☐ » donne « Dépoussiérage
 *  extérieur ». Le numéro est retiré parce que l'écran numérote déjà. */
export const operationsCollees = (texte: string): string[] => texte.split('\n')
  .map((l) => l.replace(/[☐☑☒□■✓✔✗✘]/g, ' ').replace(/^\s*\d{1,3}\s*[.)\-–:]?\s+/, '').replace(/\s+/g, ' ').trim())
  .filter(Boolean);

/**
 * La liste de contrôle d'une ligne de plan, saisie comme une liste de courses
 * (Inès, 28/10/2026) : on tape une opération, Entrée l'ajoute à la liste sous
 * forme de case à cocher, et l'on recommence. Coller plusieurs lignes (un
 * tableau Word) en ajoute autant d'un coup. L'ordre est celui de la procédure :
 * il se change en faisant glisser la poignée.
 */
function ListeOperations({ id, valeur, onChange }: { id: string; valeur: string[]; onChange: (v: string[]) => void }) {
  const [saisie, setSaisie] = useState('');
  const [tire, setTire] = useState<number | null>(null);
  const ajouter = (texte: string) => {
    const neuves = operationsCollees(texte).filter((o) => !valeur.includes(o));
    if (neuves.length) onChange([...valeur, ...neuves]);
    setSaisie('');
  };
  const deplacer = (de: number, vers: number) => {
    if (de === vers) return;
    const v = [...valeur];
    const [x] = v.splice(de, 1);
    v.splice(vers, 0, x);
    onChange(v);
  };
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      {valeur.length > 0 && (
        <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 2 }}>
          {valeur.map((o, i) => (
            <li key={`${i}-${o}`} draggable
              onDragStart={() => setTire(i)} onDragEnd={() => setTire(null)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); if (tire !== null) deplacer(tire, i); setTire(null); }}
              style={{
                display: 'flex', gap: 8, alignItems: 'center', padding: '3px 6px',
                borderRadius: 'var(--radius-sm)', background: tire === i ? 'var(--surface-sunken)' : 'transparent',
              }}>
              <span aria-hidden style={{ cursor: 'grab', color: 'var(--text-muted)', display: 'flex' }}><I n="grip" s={13} /></span>
              <input type="checkbox" disabled aria-hidden tabIndex={-1} />
              <input value={o} aria-label={`Opération ${i + 1}`}
                onChange={(e) => onChange(valeur.map((x, j) => (j === i ? e.target.value : x)))}
                onBlur={(e) => { if (!e.target.value.trim()) onChange(valeur.filter((_, j) => j !== i)); }}
                style={{ ...inputStyle, flex: 1, border: '1px solid transparent', background: 'transparent', padding: '3px 6px' }} />
              <Bouton taille="sm" variante="discret" aria-label={`Retirer « ${o} »`}
                onClick={() => onChange(valeur.filter((_, j) => j !== i))}>
                <I n="x" s={12} />
              </Bouton>
            </li>
          ))}
        </ol>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '0 6px' }}>
        <span style={{ width: 13 }} />
        <input type="checkbox" disabled aria-hidden tabIndex={-1} style={{ opacity: 0.4 }} />
        <input id={id} value={saisie} onChange={(e) => setSaisie(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ajouter(saisie); } }}
          onPaste={(e) => {
            const t = e.clipboardData.getData('text');
            if (t.includes('\n')) { e.preventDefault(); ajouter(t); }
          }}
          onBlur={() => { if (saisie.trim()) ajouter(saisie); }}
          placeholder={valeur.length ? 'Ajouter une opération, puis Entrée' : 'Première opération, puis Entrée'}
          style={{ ...inputStyle, flex: 1 }} />
      </div>
    </div>
  );
}

export function FormulaireLigne({ reg, initial, editId, equipementFixe, onFerme, onFait }: {
  reg: MaintenanceRegistre;
  initial: Brouillon;
  editId: string | null;
  equipementFixe?: string;
  onFerme: () => void;
  onFait: () => void;
}) {
  const { smq, settings } = useGed();
  const { busy, run } = useBusy();
  const [f, setF] = useState<Brouillon>(initial);
  const [apercu, setApercu] = useState<{ resume: string; anomalies: string[]; occurrences: { date: string; deplacee: boolean; motifReport: string }[]; fondement: string } | null>(null);

  const freq = reg.frequences.find((x) => x.code === f.frequence) || null;
  const set = <K extends keyof Brouillon>(k: K, v: Brouillon[K]) => setF((p) => ({ ...p, [k]: v }));

  const ancrage = useMemo(() => ({
    joursSemaine: f.joursSemaine, joursMois: f.joursMois, moisAncre: f.moisAncre,
    heure: f.heure || null, moment: f.moment || null,
  }), [f.joursSemaine, f.joursMois, f.moisAncre, f.heure, f.moment]);

  /* L'aperçu montre les DATES que produira la ligne, avant de l'enregistrer :
     « le 30 » en février, un dimanche reporté, la phase d'un trimestriel. Sans
     lui, le laboratoire découvre son plan un mois plus tard sur le calendrier. */
  useEffect(() => {
    if (!f.frequence) return undefined;
    let vivant = true;
    const p = new URLSearchParams({ frequence: f.frequence, ancrage: JSON.stringify(ancrage) });
    if (f.politiqueReport) p.set('politiqueReport', f.politiqueReport);
    if (f.debutLe) p.set('debutLe', f.debutLe);
    if (f.finLe) p.set('finLe', f.finLe);
    void fetch(`/api/ged/ged/maintenances/apercu?${p.toString()}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (vivant) setApercu(d); })
      .catch(() => { if (vivant) setApercu(null); });
    return () => { vivant = false; };
  }, [f.frequence, ancrage, f.politiqueReport, f.debutLe, f.finLe]);

  const basculeJour = (n: number) => set('joursSemaine', f.joursSemaine.includes(n) ? f.joursSemaine.filter((x) => x !== n) : [...f.joursSemaine, n].sort());
  const basculeQuantieme = (n: number) => set('joursMois', f.joursMois.includes(n) ? f.joursMois.filter((x) => x !== n) : [...f.joursMois, n].sort((a, b) => a - b));

  const corpsDe = (x: Brouillon): Record<string, unknown> => ({
    equipementCode: equipementFixe || x.equipementCode,
    libelle: x.libelle, nature: x.nature, paillasse: x.paillasse,
    frequence: x.frequence,
    ancrage: {
      joursSemaine: x.joursSemaine, joursMois: x.joursMois, moisAncre: x.moisAncre,
      heure: x.heure || null, moment: x.moment || null,
    },
    operations: x.operations.map((o) => o.trim()).filter(Boolean),
    responsableKey: x.responsableKey,
    responsableLibelle: (settings?.gedUsers || []).find((u) => u.id === x.responsableKey)?.nom || '',
    posteResponsable: x.posteResponsable,
    politiqueReport: x.politiqueReport || null,
    debutLe: x.debutLe || null, finLe: x.finLe || null,
    sourceDocument: x.sourceDocument, observation: x.observation, actif: x.actif,
  });
  /* ⚠️ CE QUE L'ÉCRAN A LU, FIGÉ À L'OUVERTURE (14/10/2026), sous la forme
     exacte du corps : en modification, le moteur n'écrit que les rubriques
     changées depuis cette lecture, et refuse (409) celles qu'un autre a
     changées entre-temps. */
  const [base, setBase] = useState<Record<string, unknown>>(() => corpsDe(initial));

  const enregistrer = () => run('save', async () => {
    const corps = corpsDe(f);
    if (editId) corps.base = base;
    const ok = await smq(editId ? `ged/maintenances/${editId}` : 'ged/maintenances', corps,
      editId ? 'Ligne de plan modifiée.' : 'Maintenance planifiée.');
    if (!ok) return;
    onFait();
    /* EN MODIFICATION, LA FENÊTRE RESTE OUVERTE (30/10/2026) : le toast dit que
       c'est pris en compte, et on enchaîne les retouches sans rouvrir la ligne.
       La lecture figée devient ce qui vient d'être écrit — sinon le second
       enregistrement serait refusé (409) comme une modification concurrente.
       Une CRÉATION se ferme : y rester ferait créer la ligne une seconde fois. */
    if (editId) setBase(corps); else onFerme();
  });

  return (
    /* Modale COMMUNE (27/10/2026) : la fenêtre faite à la main n'avait pas de
       croix et Échap la fermait sans rien demander, saisie perdue. */
    <Modale titre={editId ? 'Modifier la ligne de plan' : 'Planifier une maintenance'} largeur={760} onClose={onFerme}
      pied={<PiedActions taille="sm" annuler={{ onClick: onFerme }} principale={{ libelle: 'Enregistrer', disabled: busy === 'save' || !f.libelle || !f.frequence || !(equipementFixe || f.equipementCode) || !!apercu?.anomalies.length, onClick: enregistrer }} />}>
      <div style={{ display: 'grid', gap: 14 }}>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          {!equipementFixe && (
            <div>
              <label style={lbl} htmlFor="mnt-eq">Équipement</label>
              {/* Les automates d'abord : c'est d'eux que parle le classeur, et
                  ils sont 28 sur les 237 fiches du parc. */}
              <Selecteur id="mnt-eq" value={f.equipementCode} onChange={(v) => set('equipementCode', v)}>
                <option value="">— choisir —</option>
                {reg.parc.slice().sort((a, b) => (a.categorie === 'automate' ? 0 : 1) - (b.categorie === 'automate' ? 0 : 1) || a.code.localeCompare(b.code)).map((e) => (
                  <option key={e.code} value={e.code}>{e.code} — {e.nom}{e.categorie === 'automate' ? ' (automate)' : ''}</option>
                ))}
              </Selecteur>
            </div>
          )}
          <div>
            <label style={lbl} htmlFor="mnt-pail">Paillasse</label>
            <input id="mnt-pail" value={f.paillasse} onChange={(e) => set('paillasse', e.target.value)}
              style={inputStyle} placeholder="Biochimie, Hématologie…" />
          </div>
          <div>
            <label style={lbl} htmlFor="mnt-nature">Nature</label>
            <Selecteur id="mnt-nature" value={f.nature} onChange={(v) => set('nature', v)}>
              <option value="maintenance">Maintenance</option>
              <option value="entretien">Entretien</option>
              <option value="verification">Vérification</option>
            </Selecteur>
          </div>
        </div>

        <div>
          <label style={lbl} htmlFor="mnt-lib">Libellé de l’acte</label>
          <input id="mnt-lib" value={f.libelle} onChange={(e) => set('libelle', e.target.value)}
            style={inputStyle} placeholder="Maintenance journalière à l’ouverture" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <div>
            <label style={lbl} htmlFor="mnt-freq">Fréquence</label>
            <Selecteur id="mnt-freq" value={f.frequence} onChange={(v) => set('frequence', v)}>
              <option value="">— choisir —</option>
              {reg.frequences.map((x) => <option key={x.code} value={x.code}>{x.libelle}</option>)}
            </Selecteur>
            {freq && (
              <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
                {freq.ancrageAttendu}
                {/* La tolérance vient du paramétrage GLOBAL, par fréquence : on
                    la rappelle ici pour que le choix se fasse en connaissance,
                    et on dit quand elle manque au lieu d'afficher un zéro. */}
                {' · '}
                {freq.toleranceJours === null
                  ? <span style={{ color: 'var(--amber-600)' }}>aucun délai de tolérance paramétré</span>
                  : `tolérance de ${freq.toleranceJours} jour(s)`}
              </p>
            )}
          </div>
          <div>
            <label style={lbl} htmlFor="mnt-heure">Heure (indicative)</label>
            <input id="mnt-heure" type="time" value={f.heure} onChange={(e) => set('heure', e.target.value)} style={inputStyle} />
            <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
              Reprise du classeur (« Matin 7h »). N’entre dans aucun calcul : l’échéance se compte en jours.
            </p>
          </div>
        </div>

        {f.frequence === 'hebdomadaire' && (
          <div>
            <span style={lbl}>Jours de la semaine</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {JOURS.map((j) => (
                <Bouton variante={f.joursSemaine.includes(j.n) ? 'primaire' : 'contour'} taille="sm" key={j.n} onClick={() => basculeJour(j.n)}>{j.court}</Bouton>
              ))}
            </div>
          </div>
        )}

        {!!freq?.pasMois && (
          <div style={{ display: 'grid', gap: 10 }}>
            <div>
              <span style={lbl}>Quantièmes du mois</span>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {Array.from({ length: 31 }).map((_, i) => (
                  <Bouton variante={f.joursMois.includes(i + 1) ? 'primaire' : 'contour'} taille="sm" key={i + 1} onClick={() => basculeQuantieme(i + 1)} style={{ minWidth: 28 }}>{i + 1}</Bouton>
                ))}
              </div>
              {f.joursMois.some((q) => q > 28) && (
                <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
                  Les mois plus courts ramènent ce quantième à leur dernier jour — l’occurrence n’est jamais perdue.
                </p>
              )}
            </div>
            {freq.pasMois > 1 && (
              <div style={{ maxWidth: 240 }}>
                <label style={lbl} htmlFor="mnt-ancre">Mois d’ancrage</label>
                <Selecteur id="mnt-ancre" value={String(f.moisAncre)} onChange={(v) => set('moisAncre', Number(v))}>
                  {MOIS.map((m, i) => <option key={m} value={String(i + 1)}>{m}</option>)}
                </Selecteur>
                <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
                  Il fixe la phase du cycle : tous les {freq.pasMois} mois à partir de ce mois-là.
                </p>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <div>
            <label style={lbl} htmlFor="mnt-poste-resp">Poste responsable</label>
            <Selecteur id="mnt-poste-resp" value={f.posteResponsable} onChange={(v) => set('posteResponsable', v)}>
              <option value="">— déduit des habilitations de l’appareil —</option>
              {reg.calendrier.postesDisponibles.map((p) => <option key={p.code} value={p.code}>{p.libelle}</option>)}
            </Selecteur>
            <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
              Si vous désignez un poste, c’est la personne planifiée à CE poste ce jour-là qui répond de la
              maintenance, quelle que soit sa nature.
            </p>
          </div>
          <div>
            <label style={lbl} htmlFor="mnt-resp">Responsable de l’acquittement</label>
            <Selecteur id="mnt-resp" value={f.responsableKey} onChange={(v) => set('responsableKey', v)}>
              <option value="">— aucun : la personne planifiée au poste en répond —</option>
              {(settings?.gedUsers || []).map((u) => <option key={u.id} value={u.id}>{u.nom}</option>)}
            </Selecteur>
            {/* Arbitrage du 25/08/2026, appliqué par `responsableDeMaintenance` :
                le planning du jour décide, le nom posé ici ne sert qu'à défaut,
                et une tâche sans personne ne va jamais au RQ. */}
            <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
              Pour une maintenance préventive, la personne planifiée au poste de l’appareil ce jour-là
              en répond. Le nom posé ici ne sert qu’à défaut. Si personne n’est planifié, c’est le
              planning qu’il faut corriger.
            </p>
          </div>
          <div>
            <label style={lbl} htmlFor="mnt-report">Jour non ouvré — pour cette ligne</label>
            <Selecteur id="mnt-report" value={f.politiqueReport} onChange={(v) => set('politiqueReport', v)}>
              <option value="">Suivre le paramétrage global</option>
              {reg.politiques.map((p) => <option key={p.code} value={p.code}>{p.libelle}</option>)}
            </Selecteur>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <div>
            <label style={lbl} htmlFor="mnt-deb">Applicable à partir du</label>
            <input id="mnt-deb" type="date" value={f.debutLe} onChange={(e) => set('debutLe', e.target.value)} style={inputStyle} />
            {/* Laisser vide ne veut PAS dire « depuis toujours » : une ligne
                sans date part du jour de sa déclaration. Sinon planifier une
                bi-mensuelle ce matin fabriquerait six mois d'échéances « en
                retard » que personne n'a manquées. */}
            <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
              Vide = à partir d’aujourd’hui. Antidatez seulement si le plan s’appliquait déjà au classeur :
              les échéances passées apparaîtront alors comme non faites.
            </p>
          </div>
          <div>
            <label style={lbl} htmlFor="mnt-fin">Jusqu’au (facultatif)</label>
            <input id="mnt-fin" type="date" value={f.finLe} onChange={(e) => set('finLe', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={lbl} htmlFor="mnt-src">Document source</label>
            <input id="mnt-src" value={f.sourceDocument} onChange={(e) => set('sourceDocument', e.target.value)}
              style={inputStyle} placeholder="ENR.J1.01" />
          </div>
        </div>

        <div>
          <label style={lbl} htmlFor="mnt-ops">Liste de contrôle</label>
          <ListeOperations id="mnt-ops" valeur={f.operations} onChange={(v) => set('operations', v)} />
        </div>

        {/* Aperçu : les dates que produira la ligne. C'est ici qu'on voit que
            « le 30 » tombe le 28 en février, et qu'un dimanche a bougé. */}
        {apercu && (
          <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--surface-sunken)', display: 'grid', gap: 6 }}>
            <span style={lbl}>Aperçu</span>
            {apercu.anomalies.length > 0
              ? <div style={{ fontSize: 12.5, color: 'var(--amber-600)' }}>{apercu.anomalies.join(' ')}</div>
              : (
                <>
                  <div style={{ fontSize: 12.5, fontWeight: 600 }}>{apercu.resume}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{apercu.fondement}</div>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    {apercu.occurrences.slice(0, 14).map((o) => (
                      <span key={o.date} title={o.motifReport}
                        style={{ fontFamily: 'var(--font-mono)', fontSize: 11, padding: '2px 7px', borderRadius: 'var(--radius-xs)', cursor: 'help', background: o.deplacee ? 'var(--amber-50)' : 'var(--surface-card)', border: '1px solid var(--border-subtle)', color: o.deplacee ? 'var(--amber-600)' : 'var(--text-body)' }}>
                        {o.date}{o.deplacee ? ' ↦' : ''}
                      </span>
                    ))}
                    {apercu.occurrences.length > 14 && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>+{apercu.occurrences.length - 14}</span>}
                    {!apercu.occurrences.length && <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>aucune occurrence sur la fenêtre affichée</span>}
                  </div>
                </>
              )}
          </div>
        )}
      </div>
    </Modale>
  );
}

/* ══════════════════════════════════════════════════════════════ l'écran */

export function Maintenances({ equipement, onVersVie }: { equipement?: string; onVersVie?: () => void }) {
  const { session, saveSettings, smq, loadMaintenances } = useGed();
  const [reg, setReg] = useState<MaintenanceRegistre | null>(null);
  const [chargement, setChargement] = useState(true);
  /* L'onglet vit dans l'ADRESSE (relevé P0.8) : « la vue Paramétrage » se
     partage par lien, F5 y revient, et « Précédent » ramène à l'onglet
     précédent au lieu de quitter l'écran. */
  // Dans le dossier d'un appareil, on arrive sur ce qui est PROGRAMMÉ (29/10/2026).
  const [vue, setVue] = useOngletAdresse<Vue>(equipement ? 'plan' : 'echeances', VUES);
  // Un sous-onglet = une arrivée : on remonte (règle du 24/08/2026, cf. le crochet).
  useRemonteEnHaut(equipement ? null : vue);
  const [q, setQ] = useState('');
  // Le point de rendez-vous posé par le centre des tâches (cf. l'effet plus bas).
  const refCible = useGed((st) => st.refCible);
  const setRefCible = useGed((st) => st.setRefCible);
  const [filtre, setFiltre] = useState('');
  const [acq, setAcq] = useState<MaintenanceOccurrence | null>(null);
  /* LA PORTE UNIQUE (29/10/2026) : « Saisir une maintenance » demande d'abord le type —
     préventive au plan (on acquitte), hors plan, curative, étalonnage. `tourHisto`
     relit l'historique après une saisie, qu'il soit ouvert ou non. */
  const [saisir, setSaisir] = useState(false);
  const [tourHisto, setTourHisto] = useState(0);
  // Tri du tableau des échéances. Le code d'inventaire et le nom de l'appareil
  // tenaient dans une seule cellule (« EQ03 Erba Lyte Pro ») : la colonne ne se
  // triait alors ni par l'un ni par l'autre. Elles sont désormais deux, et
  // chacune se trie. L'échéance reste le tri par défaut — c'est la question que
  // l'écran pose en premier.
  /* L4 (10/10/2026) : le tri vit dans `<Tableau>` — plus d'état ni de
     `SortableTh` ici. L'ordre PAR DÉFAUT reste l'échéance : c'est l'ordre des
     données qu'on lui donne. */
  const [form, setForm] = useState<{ initial: Brouillon; editId: string | null } | null>(null);
  const peutParc = peutGererParc(session.roles);
  const suffixe = equipement ? `?equipement=${encodeURIComponent(equipement)}` : '';

  const charger = useCallback(async () => {
    const r = await fetch(`/api/ged/ged/maintenances${suffixe}`, { credentials: 'include' });
    const d = r.ok ? await r.json() : null;
    setReg(d);
    setChargement(false);
    // Le tableau de bord lit la même projection : on la rafraîchit aussi, sinon
    // son calendrier garde une échéance qu'on vient d'acquitter.
    await loadMaintenances();
  }, [suffixe, loadMaintenances]);

  /* L6 (10/10/2026) : un échec de lecture se DIT et se REPREND (règle 8) —
     `tour` relance l'effet depuis le bouton « Réessayer ». */
  const [tour, setTour] = useState(0);
  useEffect(() => {
    let vivant = true;
    void fetch(`/api/ged/ged/maintenances${suffixe}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (vivant) setReg(d); })
      .catch(() => { if (vivant) setReg(null); })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [suffixe, tour]);

  /* 533 lignes de plan montaient d'un bloc, sur quarante-cinq écrans (relevé
     du 27/08/2026, P2). Le hook est appelé ICI, avant les retours anticipés du
     chargement : un hook posé après eux ne serait pas appelé au même rang d'un
     rendu à l'autre, et React refuse. D'où le repli sur un tableau vide. */
  /* Une ligne RETIRÉE du plan (archivée) ne se montre plus par défaut
     (Ines, 29/10/2026 : « en supprimer ») : elle sort de la liste, sans
     s'effacer — ses acquittements sont la fiche de vie de l'appareil. */
  const [voirRetirees, setVoirRetirees] = useState(false);
  const lignesVues = useMemo(() => (reg?.lignes ?? []).filter((l) => l.actif || voirRetirees), [reg, voirRetirees]);
  const pagesReg = usePagination(lignesVues);

  /* ── ATTERRIR SUR L'ÉCHÉANCE, PAS SUR LA LISTE (retour Ines du 17/08) ──────
   *
   * Le centre des tâches pose `?ref=<planId>@<date>` : le couple identifie une
   * occurrence et une seule. On la retient ici, puis on CONSOMME la référence —
   * revenir sur l'écran plus tard ne doit pas rouvrir la même échéance.
   *
   * La pose est DIFFÉRÉE d'une micro-tâche : un `setState` synchrone en tête
   * d'effet déclenche un rendu en cascade, que React signale et que le lint du
   * dépôt refuse. Même précaution que le chargement du détail CQI. */
  const [cible, setCible] = useState<{ planId: string; date: string } | null>(null);
  useEffect(() => {
    if (!refCible || !refCible.includes('@')) return undefined;
    const [planId, date] = refCible.split('@');
    if (!planId || !date) return undefined;
    const t = setTimeout(() => {
      setCible({ planId, date });
      // On dégage les filtres : arriver sur une échéance masquée par le filtre
      // en cours donnerait une liste vide et l'impression d'un lien mort.
      setFiltre(''); setQ('');
      setRefCible(null);
    }, 0);
    return () => clearTimeout(t);
  }, [refCible, setRefCible]);

  const occurrences = useMemo(() => {
    if (!reg) return [];
    const qq = q.trim().toLowerCase();
    const retenues = reg.occurrences.filter((o) => {
      if (filtre === 'du' && !['due', 'toleree', 'en_retard'].includes(o.etat)) return false;
      if (filtre === 'retard' && o.etat !== 'en_retard') return false;
      if (filtre === 'faites' && !['faite', 'faite_tard'].includes(o.etat)) return false;
      if (filtre === 'moi' && o.responsableKey !== session.key) return false;
      if (!qq) return true;
      return `${o.equipementCode} ${o.equipementNom} ${o.libelle} ${o.paillasse}`.toLowerCase().includes(qq);
    });
    // L'ordre par défaut : l'échéance, puis l'heure. `filter` a déjà rendu un
    // tableau neuf : trier ici ne touche pas à la projection du serveur.
    const triees = retenues.sort((a, b) => `${a.date} ${a.heure || ''}`.localeCompare(`${b.date} ${b.heure || ''}`, 'fr'));
    /* L'échéance désignée passe EN TÊTE plutôt que d'être seule affichée : on
       arrive dessus, mais on voit aussi son voisinage — les autres échéances du
       jour, celles du même appareil. Une liste réduite à une ligne perdrait ce
       contexte, qui est justement ce qu'on regarde avant d'acquitter. */
    if (!cible) return triees;
    const i = triees.findIndex((o) => o.planId === cible.planId && o.date === cible.date);
    return i <= 0 ? triees : [triees[i], ...triees.slice(0, i), ...triees.slice(i + 1)];
  }, [reg, q, filtre, session.key, cible]);

  const ligneDe = (ref: string) => reg?.lignes.find((l) => l.ref === ref);

  if (chargement) return <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Chargement du plan de maintenance…</p>;

  if (!reg) {
    return (
      <EmptyState titre="Le plan de maintenance n’a pas pu être lu."
        indice="La requête n’a pas abouti. Ce n’est pas la même chose qu’un registre vide : rien n’est affirmé sur son contenu."
        action={<Bouton taille="sm" onClick={() => { setChargement(true); setTour((t) => t + 1); }}>Réessayer</Bouton>} />
    );
  }

  const enRetard = reg.occurrences.filter((o) => o.etat === 'en_retard').length;
  const aFaire = reg.occurrences.filter((o) => ['due', 'toleree'].includes(o.etat)).length;
  const sansTolerance = reg.frequences.filter((f) => f.toleranceJours === null
    && reg.lignes.some((l) => l.frequence === f.code)).length;

  const corps = (
    <>
      {session.roles.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Bouton variante="primaire" taille="sm" onClick={() => setSaisir(true)}>
            <I n="plus" s={13} />Saisir une maintenance
          </Bouton>
        </div>
      )}
      {/* DANS LE DOSSIER D'UN APPAREIL (Ines, 29/10/2026) : l'onglet Maintenance
          gère ce qui est PROGRAMMÉ — planifier, modifier, retirer du plan,
          acquitter une échéance. Ce qui a été RÉALISÉ se lit dans la fiche de
          vie, avec les NC et la métrologie : l'historique n'est donc plus un
          onglet ici (il le reste sur l'écran général des maintenances). */}
      {equipement && (
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Les maintenances <strong>programmées</strong> de cet appareil. Celles qui ont été réalisées sont dans la
          {onVersVie
            ? <> <Bouton taille="sm" variante="discret" onClick={onVersVie}>fiche de vie →</Bouton></>
            : ' fiche de vie.'}
        </p>
      )}
      <OngletsDossier id="mnt" actif={equipement && vue === 'historique' ? 'plan' : vue} onChange={setVue} items={[
        ...(equipement ? [{ k: 'plan' as Vue, label: 'Programmées', n: reg.lignes.filter((l) => l.actif).length || undefined }] : []),
        { k: 'echeances' as Vue, label: 'Échéances', n: aFaire + enRetard || undefined },
        ...(equipement ? [] : [{ k: 'plan' as Vue, label: 'Plan', n: reg.lignes.length || undefined }]),
        // Tout ce qui a été FAIT : actes du plan et interventions des fiches de vie.
        ...(equipement ? [] : [{ k: 'historique' as Vue, label: 'Historique' }]),
        // Les propositions ne s'affichent que sur la vue d'ensemble : elles
        // portent sur des dizaines d'appareils à la fois, et les relire depuis
        // le dossier d'un seul n'aurait pas de sens.
        ...(equipement ? [] : [{ k: 'propositions' as Vue, label: 'Propositions' }]),
        ...(equipement ? [] : [{ k: 'parametrage' as Vue, label: 'Paramétrage', icone: 'gear', n: sansTolerance || undefined }]),
      ]} />

      <PanneauDossier id="mnt" actif={equipement && vue === 'historique' ? 'plan' : vue}>
        {/* ── D'où viennent les jours ouvrés. Ce bandeau n'est pas décoratif :
            sans lui, personne ne peut savoir pourquoi une échéance a bougé — ou
            pourquoi elle n'a pas bougé. */}
        <div style={{ padding: '9px 12px', borderRadius: 'var(--radius-md)', background: 'var(--surface-sunken)', fontSize: 12.5, color: 'var(--text-muted)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <I n="clock" s={13} style={{ marginTop: 1, flexShrink: 0 }} />
          <span>
            {reg.calendrier.fondement}
            {' '}
            Règle globale en cas de jour non ouvré : <strong style={{ color: 'var(--text-body)' }}>
              {reg.politiques.find((p) => p.code === reg.parametrage.report)?.libelle}
            </strong>.
          </span>
        </div>

        {/* ── COUVERTURE DU PARC (lot EQP-1) ────────────────────────────────
            Ce bandeau existe contre une lecture précise et fausse : « aucune
            échéance en retard » se lit spontanément « tout est à jour », alors
            qu'aujourd'hui cela veut dire « presque rien n'est planifié ». Les
            trois nombres sont donc donnés ENSEMBLE, et la phrase vient du
            serveur pour qu'aucun écran n'en écrive une plus flatteuse.
            Il n'est affiché que sur la vue d'ensemble : sur le dossier d'un
            appareil, un chiffrage du parc entier serait hors sujet. */}
        {!equipement && (
          <div style={{ padding: '9px 12px', borderRadius: 'var(--radius-md)', background: 'var(--surface-sunken)', fontSize: 12.5, color: 'var(--text-muted)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <I n="alert" s={13} style={{ marginTop: 1, flexShrink: 0 }} />
            <span>
              <strong style={{ color: 'var(--text-body)' }}>{reg.couverture.couverts}</strong> équipement(s) couvert(s)
              {' · '}<strong style={{ color: reg.couverture.sansPlan ? 'var(--amber-600)' : 'var(--text-body)' }}>{reg.couverture.sansPlan}</strong> sans aucun plan
              {' · '}<strong style={{ color: reg.couverture.echeanceEnRetard ? 'var(--red-600)' : 'var(--text-body)' }}>{reg.couverture.echeanceEnRetard}</strong> échéance(s) de métrologie dépassée(s)
              {' · '}<strong style={{ color: 'var(--text-body)' }}>{reg.couverture.echeanceIndeterminee}</strong> indéterminée(s)
              {reg.couverture.accessoires
                ? <> · dont <strong style={{ color: 'var(--text-body)' }}>{reg.couverture.accessoires}</strong> accessoire(s), qui se maintiennent mais ne se raccordent à rien</>
                : null}
              <br />{reg.couverture.lecture}
              {' '}{reg.couverture.lectureEcheance}
            </span>
          </div>
        )}

        {/* ⚠️ CE BANDEAU EST AMBRE, ET IL EST DISTINCT DU FOND ROUGE DU CALENDRIER.
            Au calendrier, le rouge dit « personne n'est affecté aujourd'hui » —
            un trou de planning, qui se comble en planifiant. Ici, l'ambre dit
            « cet appareil n'appartient à aucun poste » : AUCUN planning n'y
            remédiera, c'est le rattachement qui manque. Les confondre enverrait
            remplir un planning pour rien. */}
        {!equipement && reg.alerteSansPoste ? (
          <div style={{
            padding: '10px 13px', borderRadius: 'var(--radius-md)',
            background: 'var(--amber-50)', border: '1px solid var(--amber-500)',
            fontSize: 12.5, color: 'var(--text-body)', display: 'flex', gap: 8, alignItems: 'flex-start',
          }}>
            <I n="alert" s={13} style={{ marginTop: 2, flexShrink: 0, color: 'var(--amber-600)' }} />
            <span>
              {reg.alerteSansPoste}
              {(reg.sansPoste || []).length > 0 && (
                <>
                  <br />
                  <span style={{ color: 'var(--text-muted)' }}>
                    {(reg.sansPoste || []).map((x) => `${x.code} — ${x.nom}`).join(' · ')}
                  </span>
                </>
              )}
            </span>
          </div>
        ) : null}

        {vue === 'echeances' && (
          <div style={{ ...card, overflow: 'hidden' }}>
            <FilterBar q={q} onQ={setQ} placeholder="Rechercher un appareil, un acte, une paillasse…"
              total={reg.occurrences.length} affiches={occurrences.length}
              actif={filtre} onChip={(k) => setFiltre(k === filtre ? '' : k)}
              onReset={() => { setQ(''); setFiltre(''); }}
              chips={[
                { k: 'du', label: 'À faire', n: aFaire },
                { k: 'retard', label: 'En retard', n: enRetard },
                { k: 'moi', label: 'Les miennes' },
                { k: 'faites', label: 'Faites' },
              ]} />
            {reg.tronque && (
              <p style={{ margin: 0, padding: '8px 14px', fontSize: 12.5, color: 'var(--amber-600)', borderBottom: '1px solid var(--border-subtle)' }}>
                La projection a été tronquée : réduisez la fenêtre affichée. La liste ci-dessous est incomplète, et c’est dit plutôt que caché.
              </p>
            )}
            {(
                <Tableau
                  id="maintenances"
                  ajusterLargeur
                  defilementHorizontal
                  /* L6 (10/10/2026) : niveau STANDARD — filtres par colonne (échéance
                     du/au, code, nom, fréquence, responsable et état en liste), masquage,
                     état vide intégré. */
                  niveau="standard"
                  vide={{
                    titre: reg.lignes.length ? 'Aucune échéance sur cette sélection.' : 'Aucune maintenance n’est planifiée.',
                    indice: reg.lignes.length
                      ? 'Le plan existe : élargissez la recherche ou changez de filtre.'
                      : 'Le registre part vide, à dessein : les fréquences viennent du laboratoire, pas de l’outil. Reprenez-les du classeur « ENR.J1.01 » dans l’onglet Plan.',
                  }}
                  lignes={occurrences}
                  cleLigne={(o) => `${o.maintenanceRef}|${o.date}`}
                  colonnes={[
                    {
                      cle: 'date', libelle: 'Échéance', type: 'date', valeur: (o) => `${o.date} ${o.heure || ''}`,
                      rendu: (o) => (
                        <>
                          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: o.date === reg.aujourdhui ? 700 : 400 }}>{fmtDate(o.date)}</span>
                          {o.heure && <span style={{ color: 'var(--text-muted)' }}> {o.heure}</span>}
                          {o.deplacee && (
                            <span title={o.motifReport} style={{ marginInlineStart: 5, cursor: 'help', color: 'var(--amber-600)', fontWeight: 700 }}>↦</span>
                          )}
                        </>
                      ),
                    },
                    {
                      cle: 'code', libelle: 'Code', type: 'liste', valeur: (o) => o.equipementCode,
                      rendu: (o) => <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{o.equipementCode}</span>,
                    },
                    { cle: 'nom', libelle: 'Nom', valeur: (o) => o.equipementNom, style: { whiteSpace: 'normal' }, rendu: (o) => o.equipementNom },
                    {
                      cle: 'acte', libelle: 'Acte', valeur: (o) => o.libelle, style: { whiteSpace: 'normal' },
                      rendu: (o) => <>{o.libelle}{o.paillasse && <span style={{ color: 'var(--text-muted)' }}> · {o.paillasse}</span>}</>,
                    },
                    { cle: 'frequence', libelle: 'Fréquence', type: 'liste', valeur: (o) => o.frequenceLibelle, style: { color: 'var(--text-muted)' }, rendu: (o) => o.frequenceLibelle },
                    {
                      cle: 'responsable', libelle: 'Responsable', type: 'liste', valeur: (o) => o.responsableLibelle || o.responsableKey || '',
                      style: { whiteSpace: 'normal' },
                      rendu: (o) => (
                        <span style={{ color: o.responsableKey ? 'var(--text-body)' : 'var(--text-muted)', fontStyle: o.responsableKey ? undefined : 'italic' }}
                          title={o.responsableSource === 'planning'
                            ? 'Destinataire DÉRIVÉ du planning : la personne affectée ce jour-là au poste qui porte cet équipement. Un responsable posé sur la ligne de plan primerait.'
                            : undefined}>
                          {o.responsableLibelle || (o.responsableKey ? o.responsableKey : 'aucun désigné')}
                          {o.responsableSource === 'planning' && <span style={{ marginInlineStart: 5, fontSize: 12.5, fontWeight: 700, color: 'var(--cyan-700)' }}>planning</span>}
                          {/* ⚠️ LE POSTE, MÊME SANS TITULAIRE (RQ, 26/08/2026) : le poste existe et
                              n'est pas tenu (ambre), ou l'appareil n'a AUCUN poste (rouge). */}
                          {!o.responsableKey && (
                            (o.postesAOccuper || []).length ? (
                              <div style={{ fontSize: 12.5, color: 'var(--amber-600)', marginTop: 2, fontStyle: 'normal' }}>
                                poste {(o.postesAOccuper || []).join(' · ')} — non tenu ce jour-là
                              </div>
                            ) : (
                              <div style={{ fontSize: 12.5, color: 'var(--red-600)', marginTop: 2, fontStyle: 'normal', fontWeight: 600 }}>
                                aucun poste rattaché à l’appareil
                              </div>
                            )
                          )}
                        </span>
                      ),
                    },
                    {
                      cle: 'etat', libelle: 'État', type: 'liste', valeur: (o) => o.etat, style: { whiteSpace: 'normal' },
                      rendu: (o) => (
                        <>
                          <EtatPill o={o} />
                          {o.toleranceNonParametree && ['due', 'toleree', 'en_retard'].includes(o.etat) && (
                            <div style={{ fontSize: 12.5, color: 'var(--amber-600)', marginTop: 2 }}>délai non paramétré</div>
                          )}
                          {o.faitLe && (
                            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>
                              {fmtDate(o.faitLe)} · {o.faitPar}
                              {/* ⚠️ LA REPRISE SE DIT : la taire ferait passer une retranscription pour une déclaration signée. */}
                              {o.repris && (
                                <span style={{ color: 'var(--amber-600)' }}>
                                  {' '}· repris{o.saisiPar ? ` par ${o.saisiPar}` : ''}
                                </span>
                              )}
                            </div>
                          )}
                        </>
                      ),
                    },
                  ]}
                  /* Une échéance à venir ne s'acquitte pas : on ne déclare pas fait ce qui n'a pas encore lieu. */
                  actions={(o) => (!o.faitLe && o.etat !== 'a_venir'
                    ? <Bouton taille="sm" onClick={() => setAcq(o)}>Acquitter</Bouton>
                    : null)}
                />
              )}
          </div>
        )}

        {(vue === 'plan' || (equipement && vue === 'historique')) && (
          <div style={{ ...card, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderBottom: '1px solid var(--border-subtle)', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)', flex: 1 }}>
                {reg.lignes.filter((l) => l.actif).length} maintenance(s) programmée(s){equipement ? ' sur cet appareil' : ''}.
              </span>
              {reg.lignes.some((l) => !l.actif) && (
                <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 12.5, color: 'var(--text-muted)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={voirRetirees} onChange={(ev) => setVoirRetirees(ev.target.checked)} />
                  Voir les retirées du plan ({reg.lignes.filter((l) => !l.actif).length})
                </label>
              )}
              {peutParc && (
                <Bouton variante="primaire" taille="sm" onClick={() => setForm({ initial: { ...LIGNE_VIDE, equipementCode: equipement || '' }, editId: null })}>
                  <I n="plus" s={13} />Planifier une maintenance
                </Bouton>
              )}
            </div>
            {/* ── SANS AUCUNE LIGNE DE PLAN, NOMMÉS ──────────────────────
                Un compteur « 76 sans plan » ne désigne personne : les cinq
                automates que le planning du laboratoire NOMME et dont la
                colonne « Maintenance » est vide (EQ03, EQ04, EQ05 Erba Lyte
                Pro · EQ08 ABL80 Flex · EQ14 Nihon Kohden) y disparaissaient
                silencieusement. Ce n'est pas un défaut de code — c'est une
                donnée qui manque —, et le seul remède est de pouvoir la
                montrer du doigt puis la saisir d'un clic, sans rouvrir le
                classeur ni lancer de script. Le bouton ouvre le formulaire
                déjà existant, sur cet appareil. */}
            {!equipement && reg.couverture.sansPlanNommes.length > 0 && (
              <details style={{ borderTop: '1px solid var(--border-subtle)', padding: '10px 14px' }}>
                <summary style={{ fontSize: 12.5, cursor: 'pointer', color: 'var(--amber-600)', fontWeight: 700 }}>
                  {reg.couverture.sansPlanNommes.length} équipement(s) de mesure ou de métrologie
                  {' '}sans <strong>aucune</strong> ligne de plan — dont {reg.couverture.sansPlanAutomates} automate(s)
                </summary>
                <p style={{ margin: '8px 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
                  Un appareil non planifié ne peut jamais être « en retard » : son absence de ce registre n’est
                  pas une preuve de conformité. La fréquence vient du laboratoire, pas de l’outil — rien n’est
                  deviné ici. Les accessoires ne figurent pas dans cette liste ; ils sont comptés à part.
                </p>
                <div style={{ display: 'grid' }}>
                  {reg.couverture.sansPlanNommes.map((x) => (
                    <div key={x.code} style={{
                      display: 'flex', gap: 9, alignItems: 'baseline', flexWrap: 'wrap',
                      padding: '6px 0', borderTop: '1px solid var(--border-subtle)',
                    }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--text-body)' }}>{x.code}</span>
                      <span style={{ fontSize: 12.5 }}>{x.nom}</span>
                      <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{x.categorie || 'catégorie non renseignée'}</span>
                      <span style={{ flex: 1 }} />
                      {peutParc && (
                        <Bouton taille="sm" onClick={() => setForm({ initial: { ...LIGNE_VIDE, equipementCode: x.code }, editId: null })}>
                          <I n="plus" s={12} />Planifier
                        </Bouton>
                      )}
                    </div>
                  ))}
                </div>
              </details>
            )}
            {!reg.lignes.length
              ? <EmptyState titre="Aucune maintenance planifiée."
                  indice="Le registre part vide à dessein : les fréquences viennent du laboratoire, pas de l’outil. Le classeur « ENR.J1.01 » en porte une vingtaine, paillasse par paillasse." />
              : !lignesVues.length
              ? <EmptyState titre="Aucune maintenance programmée en cours."
                  indice="Toutes les lignes de plan ont été retirées. Cochez « Voir les retirées du plan » pour les relire ou en remettre une." />
              : (
                <div style={{ display: 'grid' }}>
                  {pagesReg.visibles.map((l) => (
                    /* Le clic ouvre la ligne de maintenance ; « Archiver »
                       reste, sous confirmation (Ines, 26/08/2026). */
                    <div key={l.documentId}
                      {...(peutParc
                        ? ligneOuvrable(() => setForm({ initial: brouillonDe(l), editId: l.documentId }), `Modifier ${l.ref}`,
                          { padding: '11px 14px', borderTop: '1px solid var(--border-subtle)', display: 'grid', gap: 5, opacity: l.actif ? 1 : 0.55 })
                        : { style: { padding: '11px 14px', borderTop: '1px solid var(--border-subtle)', display: 'grid', gap: 5, opacity: l.actif ? 1 : 0.55 } })}>
                      <div style={{ display: 'flex', gap: 9, alignItems: 'baseline', flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--color-primary-active)', fontWeight: 700 }}>{l.ref}</span>
                        {!equipement && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)' }}>{l.equipementCode}</span>}
                        <strong style={{ fontSize: 13 }}>{l.libelle}</strong>
                        {l.paillasse && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>· {l.paillasse}</span>}
                        {!l.actif && <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}>retirée du plan</span>}
                        <span style={{ flex: 1 }} />
                        {peutParc && (
                          <SansOuvrir>
                            {/* La ligne entière ouvre déjà le formulaire, mais rien
                                ne le disait : « je ne trouve aucun moyen de modifier
                                les maintenances planifiées » (Inès, 28/10/2026). */}
                            <Bouton taille="sm" onClick={() => setForm({ initial: brouillonDe(l), editId: l.documentId })}>
                              <I n="pen" s={12} />Modifier
                            </Bouton>
                          </SansOuvrir>
                        )}
                        {peutParc && (
                          <SansOuvrir>
                            {/* « Supprimer » une maintenance programmée = la RETIRER du
                                plan : elle cesse de produire des échéances et sort de la
                                liste. Rien ne s'efface (règle du dépôt) : ses
                                acquittements restent dans la fiche de vie. */}
                            <ConfirmButton label={l.actif ? 'Retirer du plan' : 'Remettre au plan'} variant="outline"
                              question={l.actif
                                ? 'Retirer cette maintenance du plan ? Elle cesse de produire des échéances et disparaît de la liste. Les maintenances déjà réalisées restent dans la fiche de vie de l’appareil.'
                                : 'Remettre cette maintenance au plan ? Elle recommence à produire des échéances.'}
                              onConfirm={async () => { await smq(`ged/maintenances/${l.documentId}/archiver`, { actif: !l.actif }); await charger(); }} />
                          </SansOuvrir>
                        )}
                      </div>
                      <div style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
                        {l.equipementNom && !equipement && <span style={{ color: 'var(--text-muted)' }}>{l.equipementNom} · </span>}
                        {l.resume}
                      </div>
                      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <span>{l.frequenceLibelle}</span>
                        <span>{l.responsableLibelle || (l.responsableKey || 'aucun responsable désigné')}</span>
                        {l.posteResponsable && <span>poste : {reg.calendrier.postesDisponibles.find((p) => p.code === l.posteResponsable)?.libelle || l.posteResponsable}</span>}
                        <span>{l.toleranceJours === null ? 'délai de tolérance non paramétré' : `tolérance ${l.toleranceJours} j`}</span>
                        <span>{l.nbActes} acquittement(s){l.dernierActe ? ` · dernier le ${fmtDate(l.dernierActe)}` : ''}</span>
                        {l.operations.length > 0 && <span>{l.operations.length} opération(s)</span>}
                        {l.sourceDocument && <span>source : {l.sourceDocument}</span>}
                      </div>
                      {l.anomalies.length > 0 && (
                        <div style={{ fontSize: 12.5, color: 'var(--amber-600)' }}>
                          {l.anomalies.join(' ')} — cette ligne ne produit aucune échéance en l’état.
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            <Pagination page={pagesReg.page} pages={pagesReg.pages} debut={pagesReg.debut} fin={pagesReg.fin}
              total={pagesReg.total} onPage={pagesReg.setPage} quoi="lignes de plan" />
          </div>
        )}

        {vue === 'historique' && !equipement && <MaintenancesHistorique key={tourHisto} equipement={equipement} />}
        {vue === 'propositions' && <Propositions peutParc={peutParc} onApplique={charger} />}

        {vue === 'parametrage' && <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          {/* Ce qui est tracé se VOIT (24/08/2026) : les réglages et bascules
              d'alternance s'écrivaient sous « MAINTENANCE-ALTERNANCE » sans accès. */}
          <BoutonJournal reference="MAINTENANCE-ALTERNANCE" quoi="des réglages d'alternance" />
        </div>}
        {vue === 'parametrage' && <Parametrage reg={reg} peutParc={peutParc} onEnregistre={charger} save={saveSettings} />}
      </PanneauDossier>

      {acq && <Acquitter occ={acq} ligne={ligneDe(acq.maintenanceRef)} onFerme={() => setAcq(null)} onFait={() => { setTourHisto((t) => t + 1); return charger(); }} />}
      {saisir && (
        <SaisirMaintenance equipementFixe={equipement} occurrences={reg.occurrences}
          onAcquitter={(o) => { setSaisir(false); setAcq(o); }}
          onFerme={() => setSaisir(false)}
          onFait={() => { setTourHisto((t) => t + 1); void charger(); }} />
      )}
      {form && (
        <FormulaireLigne reg={reg} initial={form.initial} editId={form.editId} equipementFixe={equipement}
          onFerme={() => setForm(null)} onFait={charger} />
      )}
    </>
  );

  // Encastré dans le dossier d'un équipement : pas de titre de page, il en a
  // déjà un. C'est la seule différence entre les deux usages.
  if (equipement) return <div style={{ display: 'grid', gap: 14 }}>{corps}</div>;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* ⚠️ La rubrique d’abord, la clause ensuite (SYS-1) : cet écran seul
          faisait l’inverse — « §6.4.4 · Plan d’entretien… » — ce qui met en
          avant un numéro de norme là où l’œil cherche à se situer. */}
      <EnteteEcran rubrique="Ressources" clauseISO="6.4.5" titre="Maintenances" />
      <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)', maxWidth: 760 }}>
        Cette page rassemble tous les paramétrages de maintenance : ce qu’il y a à faire sur chaque
        appareil, à quelle fréquence, sous quelle responsabilité, avec quel délai de tolérance et quelle
        règle les jours non ouvrés. Elle est aussi accessible depuis chaque fiche d’équipement.
      </p>
      {corps}
    </div>
  );
}

/* ═══════════════════════════════════ propositions de plan — LA RELECTURE */

/**
 * Appliquer un plan validé, depuis l'écran.
 *
 * ── Le reliquat que cet onglet referme ─────────────────────────────────────
 *
 * Cinquante-deux lignes de plan, relevées d'`INS.J2.01`, étaient prêtes et ne
 * pouvaient s'appliquer que comme ceci :
 *
 *     BIO24_JETON=<jeton RQ> node backend/scripts/reprise-plan-maintenance-lot2.js --confirm
 *
 * La responsable qualité était connectée dans son navigateur, et cela ne
 * suffisait pas : le script tourne dans un terminal, il ne voit pas son cookie.
 * Appliquer un plan validé est pourtant un geste de RQ — pas une opération
 * d'exploitation. Il vit ici, avec sa session, donc ses droits, sa validation
 * et son nom au journal chaîné de chaque appareil.
 *
 * ── Pourquoi cet onglet est une RELECTURE et non un bouton ─────────────────
 *
 * Le bouton est la plus petite partie de ce qui suit. 52 lignes sur
 * 29 appareils ne se valident pas en aveugle : on montre donc, appareil par
 * appareil, l'opération, la périodicité, LA CITATION EXACTE du document dont
 * elle vient, les dates qu'elle produira — et surtout ce qui EXISTE DÉJÀ, avec
 * la référence de la ligne en place. Une ligne déjà au plan n'est pas
 * sélectionnable : c'est ainsi que l'idempotence se voit, au lieu de se
 * promettre.
 *
 * ── Les trois valeurs que le document NE DONNE PAS ─────────────────────────
 *
 * Le script les posait en silence. Ici elles sont en haut de l'écran, avec leur
 * motif et — pour le seuil de dégivrage — les deux documents qui se
 * contredisent, chacun avec sa valeur. Les changer relance la relecture : ce
 * qui est affiché est ce qui sera écrit, jamais une approximation locale.
 */
function ChampDecision({ d, valeur, onChange, utilisateurs }: {
  d: PropositionDecision;
  valeur: string;
  onChange: (v: string) => void;
  utilisateurs: { id: string; nom: string }[];
}) {
  const id = `dec-${d.cle}`;
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <label style={lbl} htmlFor={id}>{d.libelle}</label>
      {d.type === 'choix' && (
        <Selecteur id={id} value={valeur} onChange={onChange}>
          {(d.options || []).map((o) => <option key={o.valeur} value={o.valeur}>{o.libelle} — {o.source}</option>)}
        </Selecteur>
      )}
      {d.type === 'mois' && (
        <Selecteur id={id} value={valeur} onChange={onChange}>
          {MOIS.map((m, i) => <option key={m} value={String(i + 1)}>{m}</option>)}
        </Selecteur>
      )}
      {d.type === 'responsable' && (
        <Selecteur id={id} value={valeur} onChange={onChange}>
          <option value="">— aucun : la tâche ira au responsable qualité —</option>
          {utilisateurs.map((u) => <option key={u.id} value={u.id}>{u.nom}</option>)}
        </Selecteur>
      )}
      {(d.type === 'quantieme' || d.type === 'texte') && (
        <input id={id} type={d.type === 'quantieme' ? 'number' : 'text'}
          min={d.type === 'quantieme' ? 1 : undefined} max={d.type === 'quantieme' ? 31 : undefined}
          value={valeur} onChange={(e) => onChange(e.target.value)} style={{ ...inputStyle, maxWidth: 130 }} />
      )}
      {/* ⚠️ Le motif n'est PAS une aide contextuelle : c'est la raison pour
          laquelle la valeur n'est pas sourcée. Il est écrit tel quel dans
          l'observation de chaque ligne créée, pour qu'un évaluateur le lise
          sur la ligne et non dans un code. */}
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--amber-600)', lineHeight: 1.5 }}>
        Valeur non sourcée — décision du laboratoire. {d.motif}
      </p>
      {d.consequence && (
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>{d.consequence}</p>
      )}
    </div>
  );
}

const ETATS_PROP: Record<PropositionLigne['etat'], { label: string; fg: string; bg: string }> = {
  nouvelle: { label: 'à créer', fg: 'var(--color-primary-active)', bg: 'var(--cyan-50)' },
  deja_au_plan: { label: 'déjà au plan', fg: 'var(--green-600)', bg: 'var(--green-50)' },
  appareil_inconnu: { label: 'appareil inconnu', fg: 'var(--red-600)', bg: 'var(--red-50)' },
};

function LigneProposee({ l, coche, onCoche, peut }: {
  l: PropositionLigne;
  coche: boolean;
  onCoche: (v: boolean) => void;
  peut: boolean;
}) {
  const m = ETATS_PROP[l.etat];
  // Une ligne déjà au plan, un appareil inconnu, une ligne qui ne produirait
  // aucune date : trois raisons de NE PAS pouvoir être cochée, et chacune est
  // dite juste en dessous. Une case grisée sans motif serait une énigme.
  const selectionnable = peut && l.etat === 'nouvelle' && !l.anomalies.length;
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderTop: '1px solid var(--border-subtle)' }}>
      <input type="checkbox" checked={coche} disabled={!selectionnable} onChange={(e) => onCoche(e.target.checked)}
        aria-label={`${l.equipementCode} — ${l.libelle}`} style={{ marginTop: 3 }} />
      <div style={{ flex: 1, display: 'grid', gap: 3, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 13 }}>{l.libelle}</strong>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{l.frequenceLibelle}</span>
          <span style={{ flex: 1 }} />
          <span style={{ display: 'inline-flex', padding: '2px 9px', borderRadius: 'var(--radius-pill)', background: m.bg, color: m.fg, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
            {m.label}{l.refExistante ? ` · ${l.refExistante}` : ''}
          </span>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-body)' }}>{l.resume}</div>
        {/* LA SOURCE, NOMMÉE, sur chaque ligne. C'est elle qui distingue une
            reprise documentaire d'une saisie au jugé. */}
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>
          {l.sourceDocument} — {l.citation}
        </div>
        {l.operations.length > 0 && (
          <details>
            <summary style={{ fontSize: 12.5, color: 'var(--text-muted)', cursor: 'pointer' }}>
              {l.operations.length} opération(s) à cocher à l’acquittement
            </summary>
            <ul style={{ margin: '4px 0 0', paddingInlineStart: 18, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.6 }}>
              {l.operations.map((o) => <li key={o}>{o}</li>)}
            </ul>
          </details>
        )}
        {l.anomalies.length > 0 && (
          <div style={{ fontSize: 12.5, color: 'var(--red-600)' }}>
            {l.anomalies.join(' ')} — cette ligne ne produirait aucune échéance : elle ne peut pas être appliquée.
          </div>
        )}
        {l.etat === 'deja_au_plan' && (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Cette ligne existe déjà sur cet appareil : elle est laissée en place, telle qu’elle est. Appliquer
            de nouveau ne la doublera pas et ne la modifiera pas.
          </div>
        )}
        {l.etat === 'appareil_inconnu' && (
          <div style={{ fontSize: 12.5, color: 'var(--red-600)' }}>
            Aucun équipement en service ne porte le code « {l.equipementCode} ». Le plan resterait incomplet :
            c’est le parc qu’il faut corriger, pas la proposition.
          </div>
        )}
      </div>
    </div>
  );
}

function CarteProposition({ p, peutAppliquer, selection, onSelection, onChoix, onApplique }: {
  p: Proposition;
  peutAppliquer: boolean;
  selection: Set<string>;
  onSelection: (cles: string[], coche: boolean) => void;
  onChoix: (cleDecision: string, valeur: string) => void;
  onApplique: (p: Proposition, cles: string[]) => Promise<void>;
}) {
  const { settings } = useGed();
  const { busy, run } = useBusy();
  const utilisateurs = (settings?.gedUsers || []).map((u) => ({ id: u.id, nom: u.nom }));
  const toutes = p.groupes.flatMap((g) => g.lignes);
  const applicables = toutes.filter((l) => l.etat === 'nouvelle' && !l.anomalies.length).map((l) => l.cle);
  const choisies = applicables.filter((c) => selection.has(c));

  return (
    <div style={{ ...card, overflow: 'hidden' }}>
      <div style={{ padding: '13px 15px', borderBottom: '1px solid var(--border-subtle)', display: 'grid', gap: 6 }}>
        <div style={{ display: 'flex', gap: 9, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <I n="doc" s={14} style={{ alignSelf: 'center' }} />
          <strong style={{ fontSize: 14 }}>{p.titre}</strong>
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 'var(--radius-pill)',
            background: p.sourceApprouvee ? 'var(--green-50)' : 'var(--amber-50)',
            color: p.sourceApprouvee ? 'var(--green-600)' : 'var(--amber-600)',
          }}>
            {p.sourceApprouvee ? 'document approuvé' : 'source non approuvée'}
          </span>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{p.sourceDocument}</div>
        {p.sourceNature && <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{p.sourceNature}</div>}
        {p.presentation && <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, maxWidth: 860 }}>{p.presentation}</p>}
        <div style={{ fontSize: 12.5, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <span><strong>{p.compte.nouvelles}</strong> ligne(s) à créer</span>
          <span style={{ color: 'var(--green-600)' }}><strong>{p.compte.dejaAuPlan}</strong> déjà au plan</span>
          {p.compte.appareilsInconnus > 0 && (
            <span style={{ color: 'var(--red-600)' }}><strong>{p.compte.appareilsInconnus}</strong> sur un appareil inconnu</span>
          )}
          <span style={{ color: 'var(--text-muted)' }}>{p.compte.total} au total, sur {p.compte.equipements} appareil(s)</span>
        </div>
      </div>

      {p.avertissements.length > 0 && (
        <div style={{ padding: '10px 15px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface-sunken)', display: 'grid', gap: 6 }}>
          {p.avertissements.map((a) => (
            <div key={a} style={{ fontSize: 12.5, color: 'var(--text-muted)', display: 'flex', gap: 7, lineHeight: 1.55 }}>
              <I n="info" s={13} style={{ marginTop: 2, flexShrink: 0 }} /><span>{a}</span>
            </div>
          ))}
        </div>
      )}

      {p.decisions.length > 0 && (
        <div style={{ padding: '13px 15px', borderBottom: '1px solid var(--border-subtle)', display: 'grid', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-strong)' }}>
              Ce que le document ne dit pas — {p.decisions.length} décision(s) du laboratoire
            </h3>
            <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--text-muted)', maxWidth: 860, lineHeight: 1.55 }}>
              Ces valeurs ne se trouvent nulle part dans la source. Elles sont réglées ICI, avant l’écriture,
              et la valeur retenue est inscrite dans l’observation de chaque ligne créée — avec la mention
              qu’elle n’est pas sourcée, et, s’il y a lieu, la valeur écartée et son document. Les changer
              relance la relecture : ce qui est affiché est ce qui sera écrit.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 14 }}>
            {p.decisions.map((d) => (
              <ChampDecision key={d.cle} d={d} valeur={p.choix[d.cle] ?? ''} utilisateurs={utilisateurs}
                onChange={(v) => onChoix(d.cle, v)} />
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 15px', borderBottom: '1px solid var(--border-subtle)', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', flex: 1 }}>
          {choisies.length} ligne(s) sélectionnée(s) sur {applicables.length} applicable(s).
        </span>
        {peutAppliquer && applicables.length > 0 && (
          <>
            <Bouton taille="sm" onClick={() => onSelection(applicables, choisies.length !== applicables.length)}>
              {choisies.length === applicables.length ? 'Tout décocher' : 'Tout sélectionner'}
            </Bouton>
            <ConfirmButton label={`Appliquer ${choisies.length} ligne(s)`} variant="primary"
              disabled={!choisies.length || busy === 'appli'}
              question={`Écrire ${choisies.length} ligne(s) de plan sur ${new Set(toutes.filter((l) => choisies.includes(l.cle)).map((l) => l.equipementCode)).size} appareil(s), sous votre nom et avec vos droits ? Chaque ligne sera inscrite au journal chaîné de son appareil. Les lignes déjà au plan sont laissées en place : rien ne sera doublé.`}
              onConfirm={() => run('appli', () => onApplique(p, choisies))} />
          </>
        )}
        {!peutAppliquer && (
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Seul le responsable qualité applique une proposition. Elle est affichée parce que savoir ce que
            le laboratoire s’apprête à demander fait partie du travail.
          </span>
        )}
      </div>

      <div style={{ display: 'grid' }}>
        {p.groupes.map((g) => {
          const cles = g.lignes.filter((l) => l.etat === 'nouvelle' && !l.anomalies.length).map((l) => l.cle);
          const tout = cles.length > 0 && cles.every((c) => selection.has(c));
          return (
            <div key={g.equipementCode} style={{ padding: '10px 15px', borderTop: '1px solid var(--border-subtle)' }}>
              <div style={{ display: 'flex', gap: 9, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: g.connu ? 'var(--text-body)' : 'var(--red-600)' }}>
                  {g.equipementCode}
                </span>
                <span style={{ fontSize: 12.5 }}>{g.equipementNom || (g.connu ? '' : 'absent du parc')}</span>
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{g.lignes.length} ligne(s)</span>
                <span style={{ flex: 1 }} />
                {peutAppliquer && cles.length > 0 && (
                  <Bouton taille="sm" onClick={() => onSelection(cles, !tout)}>
                    {tout ? 'décocher' : 'cocher'} cet appareil
                  </Bouton>
                )}
              </div>
              {g.lignes.map((l) => (
                <LigneProposee key={l.cle} l={l} peut={peutAppliquer} coche={selection.has(l.cle)}
                  onCoche={(v) => onSelection([l.cle], v)} />
              ))}
            </div>
          );
        })}
      </div>

      {/* ── CE QUI NE S'APPLIQUE PAS ───────────────────────────────────────
          Montré, séparé, et SANS AUCUNE CASE. Ces lignes n'ont pas de clé
          côté serveur : aucune requête, même forgée, ne peut les atteindre.
          Les cacher aurait été pire — le laboratoire doit savoir ce qui
          l'attend, et c'est en rédigeant les instructions manquantes que
          cela se règle, pas en recopiant un manuel tiers. */}
      {p.nonApplicables.length > 0 && (
        <div style={{ padding: '13px 15px', borderTop: '3px solid var(--border-subtle)', background: 'var(--surface-sunken)' }}>
          <h3 style={{ margin: 0, fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--amber-600)' }}>
            <I n="alert" s={13} style={{ display: 'inline-block', verticalAlign: '-2px', marginInlineEnd: 6 }} />
            Vu, et NON applicable — {p.nonApplicables.length} entrée(s)
          </h3>
          <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--text-muted)', maxWidth: 860, lineHeight: 1.55 }}>
            Ces lignes ne sont pas oubliées : elles ne peuvent simplement pas être appliquées, et aucune case
            ne permet de les cocher. Les écrire fabriquerait un plan d’apparence complète — opposable devant
            un évaluateur, et adossé à rien.
          </p>
          <div style={{ display: 'grid', marginTop: 6 }}>
            {p.nonApplicables.map((n) => (
              <div key={n.intitule} style={{ padding: '7px 0', borderTop: '1px solid var(--border-subtle)', display: 'grid', gap: 2 }}>
                <strong style={{ fontSize: 12.5 }}>{n.intitule}</strong>
                {n.detail && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{n.detail}</span>}
                <span style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.55 }}>{n.motif}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Propositions({ peutParc, onApplique }: { peutParc: boolean; onApplique: () => Promise<void> }) {
  const { smqData } = useGed();
  const [reg, setReg] = useState<PropositionsRegistre | null>(null);
  const [chargement, setChargement] = useState(true);
  const [echec, setEchec] = useState(false);
  // Les valeurs non sourcées retenues, PAR recueil. Le serveur les renvoie
  // telles qu'il les a comprises : l'écran n'affiche jamais ce qu'il croit
  // avoir envoyé.
  const [choix, setChoix] = useState<Record<string, Record<string, string>>>({});
  const [selection, setSelection] = useState<Set<string>>(() => new Set());
  const [bilan, setBilan] = useState<string[]>([]);

  useEffect(() => {
    let vivant = true;
    const p = Object.keys(choix).length ? `?choix=${encodeURIComponent(JSON.stringify(choix))}` : '';
    void fetch(`/api/ged/ged/maintenances/propositions${p}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (vivant) { setReg(d); setEchec(!d); setChargement(false); } })
      .catch(() => { if (vivant) { setReg(null); setEchec(true); setChargement(false); } });
    return () => { vivant = false; };
  }, [choix]);

  const majSelection = useCallback((cles: string[], coche: boolean) => {
    setSelection((s) => {
      const n = new Set(s);
      for (const c of cles) { if (coche) n.add(c); else n.delete(c); }
      return n;
    });
  }, []);

  const majChoix = useCallback((cleRecueil: string, cleDecision: string, valeur: string) => {
    setChoix((c) => ({ ...c, [cleRecueil]: { ...(c[cleRecueil] || {}), [cleDecision]: valeur } }));
  }, []);

  const appliquer = useCallback(async (p: Proposition, cles: string[]) => {
    const rep = await smqData<{
      creees: { ref: string; equipementCode: string; libelle: string }[];
      laissees: { cle: string; motif: string }[];
      planAvant: { lignes: number; equipements: number };
      planApres: { lignes: number; equipements: number };
    }>(`ged/maintenances/propositions/${encodeURIComponent(p.cle)}/appliquer`,
      { choix: p.choix, cles }, 'Proposition appliquée.');
    if (!rep) return;
    // Le bilan vient du SERVEUR, avant/après compris : c'est lui qui prouve
    // qu'un second clic n'a rien doublé, pas une phrase optimiste de l'écran.
    setBilan([
      `${rep.creees.length} ligne(s) créée(s), ${rep.laissees.length} laissée(s) en place.`,
      `Plan avant : ${rep.planAvant.lignes} ligne(s) sur ${rep.planAvant.equipements} appareil(s).`,
      `Plan après : ${rep.planApres.lignes} ligne(s) sur ${rep.planApres.equipements} appareil(s).`,
      ...rep.laissees.map((x) => `Laissée : ${x.motif}`),
    ]);
    setSelection(new Set());
    // On relance la relecture : les lignes tout juste créées doivent
    // apparaître « déjà au plan », sinon un second clic paraîtrait possible.
    setChoix((c) => ({ ...c }));
    await onApplique();
  }, [smqData, onApplique]);

  if (chargement) return <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Lecture des propositions…</p>;
  if (echec || !reg) {
    return (
      <EmptyState titre="Les propositions n’ont pas pu être lues."
        indice="La requête n’a pas abouti. Ce n’est pas la même chose qu’« aucune proposition » : rien n’est affirmé sur leur contenu." />
    );
  }

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ padding: '10px 13px', borderRadius: 'var(--radius-md)', background: 'var(--surface-sunken)', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
        Une proposition est la transcription d’un document du laboratoire, relue ici AVANT toute écriture :
        chaque ligne porte l’opération, sa périodicité, la citation exacte dont elle vient, et ce qui existe
        déjà en base. Appliquer se fait sous votre nom et avec vos droits, et s’inscrit au journal chaîné de
        chaque appareil. Rejouer n’écrit rien de plus : une ligne déjà au plan est laissée en place.
        {reg.dossier && (
          <><br />Les propositions sont lues dans <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>{reg.dossier}</span> — y déposer un fichier suffit à en proposer une nouvelle.</>
        )}
      </div>

      {reg.erreurs.length > 0 && (
        <div style={{ ...card, padding: '11px 14px', borderColor: 'var(--red-600)', display: 'grid', gap: 4 }}>
          <strong style={{ fontSize: 12.5, color: 'var(--red-600)' }}>
            {reg.erreurs.length} recueil(s) refusé(s) — ils ne sont PAS chargés à moitié.
          </strong>
          {reg.erreurs.map((e) => <span key={e} style={{ fontSize: 12.5, color: 'var(--text-body)' }}>{e}</span>)}
        </div>
      )}

      {bilan.length > 0 && (
        <div style={{ ...card, padding: '11px 14px', display: 'grid', gap: 3 }}>
          <strong style={{ fontSize: 12.5 }}>Dernière application — bilan rendu par le serveur</strong>
          {bilan.map((b) => <span key={b} style={{ fontSize: 12.5, color: 'var(--text-body)' }}>{b}</span>)}
        </div>
      )}

      {!reg.propositions.length
        ? <EmptyState titre="Aucune proposition de plan n’est déposée."
            indice="Une proposition est un fichier de recueil placé dans le dossier des propositions du serveur. Le registre n’en fabrique aucune : les fréquences viennent du laboratoire, pas de l’outil." />
        : reg.propositions.map((p) => (
          <CarteProposition key={p.cle} p={p} peutAppliquer={peutParc && reg.peutAppliquer}
            selection={selection}
            onSelection={majSelection}
            onChoix={(cle, v) => majChoix(p.cle, cle, v)}
            onApplique={appliquer} />
        ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════ paramétrage global */

/**
 * Le paramétrage GLOBAL — N4-3 pour l'essentiel.
 *
 * Le délai de tolérance se règle PAR FRÉQUENCE, et c'est le point : une
 * maintenance journalière faite avec deux jours de retard n'a pas été faite,
 * une annuelle avec deux jours de retard a été faite. Un délai unique
 * mentirait dans un sens ou dans l'autre.
 *
 * Une case laissée VIDE n'est pas un zéro : elle veut dire « le laboratoire n'a
 * pas fixé de délai », et alors aucune échéance de cette fréquence n'est jamais
 * déclarée en retard. On le dit sous chaque case.
 */
function Parametrage({ reg, peutParc, onEnregistre, save }: {
  reg: MaintenanceRegistre;
  peutParc: boolean;
  onEnregistre: () => Promise<void>;
  save: (patch: { maintenance: Record<string, unknown> }) => Promise<boolean>;
}) {
  const { busy, run } = useBusy();
  const [tol, setTol] = useState<Record<string, string>>(() => Object.fromEntries(
    reg.frequences.map((f) => [f.code, f.toleranceJours === null ? '' : String(f.toleranceJours)]),
  ));
  const [report, setReport] = useState(reg.parametrage.report);
  /* Surcharge PAR FRÉQUENCE (14/08/2026). Case vide = « suit la règle
     générale » ; c'est différent de « aucun », qui est le choix explicite de
     ne jamais déplacer cette fréquence-là. */
  const [reports, setReports] = useState<Record<string, string>>(() => {
    const d: Record<string, string> = {};
    for (const [k, v] of Object.entries(reg.parametrage.reports || {})) if (v) d[k] = v;
    return d;
  });
  const [poste, setPoste] = useState(reg.parametrage.posteReference || '');
  const [antic, setAntic] = useState(String(reg.parametrage.anticipationJours));

  const enregistrer = () => run('param', async () => {
    const tolerances: Record<string, number> = {};
    for (const [k, v] of Object.entries(tol)) {
      // Case vide = NON PARAMÉTRÉE : on ne l'écrit pas. Écrire `0` à la place
      // signifierait « aucun retard toléré », ce qui n'est pas ce qu'on a dit.
      if (v.trim() === '') continue;
      const n = Number(v);
      if (Number.isFinite(n) && n >= 0) tolerances[k] = Math.trunc(n);
    }
    const surcharges: Record<string, string> = {};
    for (const [k, v] of Object.entries(reports)) if (v) surcharges[k] = v;
    const ok = await save({
      maintenance: {
        tolerances, report, reports: surcharges,
        posteReference: poste || null, anticipationJours: Number(antic) || 7,
      },
    });
    if (ok) await onEnregistre();
  });

  return (
    <div style={{ ...card, padding: 18, display: 'grid', gap: 18 }}>
      <section style={{ display: 'grid', gap: 10 }}>
        <h3 style={{ margin: 0, fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-strong)' }}>
          Délai de tolérance, par fréquence
        </h3>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', maxWidth: 700 }}>
          Combien de jours après l’échéance un acte reste-t-il « dans les temps » ? Une case laissée
          vide signifie que le laboratoire n’a pas fixé de délai : aucune échéance de cette fréquence
          ne sera alors déclarée en retard, et l’écran le dira. Aucune valeur n’est proposée par
          défaut — ce serait décider à votre place de ce qui constitue un manquement.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12 }}>
          {reg.frequences.map((f) => {
            const utilisee = reg.lignes.filter((l) => l.frequence === f.code).length;
            return (
              <div key={f.code}>
                <label style={lbl} htmlFor={`tol-${f.code}`}>{f.libelle}</label>
                <input id={`tol-${f.code}`} type="number" min={0} disabled={!peutParc}
                  value={tol[f.code] ?? ''} onChange={(e) => setTol((p) => ({ ...p, [f.code]: e.target.value }))}
                  style={{ ...inputStyle, maxWidth: 130 }} placeholder="non paramétré" />
                <p style={{ margin: '4px 0 0', fontSize: 12.5, color: (tol[f.code] ?? '') === '' ? 'var(--amber-600)' : 'var(--text-muted)' }}>
                  {(tol[f.code] ?? '') === '' ? 'aucun retard qualifiable' : 'jours'}
                  {utilisee ? ` · ${utilisee} ligne(s)` : ''}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      <section style={{ display: 'grid', gap: 10, borderTop: '1px solid var(--border-subtle)', paddingTop: 16 }}>
        <h3 style={{ margin: 0, fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-strong)' }}>
          Échéance tombant un jour non ouvré
        </h3>
        {/* ⚠️ Le passage le plus important de cet écran. La règle « si férié,
            alors reporter » est FAUSSE dans ce laboratoire : il travaille les
            fériés et tourne du lundi au samedi. On ne code donc aucun jour :
            c'est le calendrier d'ouverture d'un poste du planning RH qui
            répond, et c'est le laboratoire qui désigne lequel. */}
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', maxWidth: 700 }}>
          Les jours ouvrés ne sont pas devinés : ils viennent du calendrier d’ouverture d’un poste du
          planning RH, fériés et régimes spéciaux compris. Ce laboratoire travaille les jours fériés
          et tourne du lundi au samedi — une règle « si férié, alors reporter » y déplacerait des
          centaines d’échéances à tort. Sans poste de référence, aucune échéance n’est jamais déplacée.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
          <div>
            <label style={lbl} htmlFor="mnt-poste">Poste de référence</label>
            <Selecteur id="mnt-poste" value={poste} onChange={setPoste} disabled={!peutParc}>
              <option value="">— aucun : aucune échéance déplacée —</option>
              {reg.calendrier.postesDisponibles.map((p) => <option key={p.code} value={p.code}>{p.libelle}</option>)}
            </Selecteur>
          </div>
          <div>
            <label style={lbl} htmlFor="mnt-pol">Règle appliquée</label>
            <Selecteur id="mnt-pol" value={report} onChange={(v) => setReport(v as typeof report)} disabled={!peutParc}>
              {reg.politiques.map((p) => <option key={p.code} value={p.code}>{p.libelle}</option>)}
            </Selecteur>
            <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
              {reg.politiques.find((p) => p.code === report)?.aide}
            </p>
          </div>
          <div>
            <label style={lbl} htmlFor="mnt-antic">Anticipation au centre de tâches</label>
            <input id="mnt-antic" type="number" min={1} value={antic} disabled={!peutParc}
              onChange={(e) => setAntic(e.target.value)} style={{ ...inputStyle, maxWidth: 130 }} />
            <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
              jours à l’avance. Les échéances passées non acquittées restent visibles quoi qu’il arrive.
            </p>
          </div>
        </div>
        {/* ── La règle par fréquence ────────────────────────────────────────
            Reporter n'a pas le même sens selon le rythme. Une MENSUELLE tombant
            un dimanche doit se faire : il n'y en a qu'une par mois, la sauter
            c'est la sauter tout court. Une JOURNALIÈRE, non : celle du lundi
            arrive de toute façon, et la reporter en créerait deux le même jour
            — puis un faux retard. Une règle unique obligeait à choisir entre
            ces deux erreurs. */}
        <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12, display: 'grid', gap: 8 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>
            Exception par fréquence
          </div>
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', maxWidth: 700, lineHeight: 1.5 }}>
            Une fréquence peut suivre une autre règle que la règle générale. Laisser « suit la règle
            générale » n’est pas la même chose que choisir « ne pas déplacer » : le premier suit vos
            changements futurs, le second est une décision prise pour cette fréquence.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 12 }}>
            {reg.frequences.map((f) => (
              <div key={f.code}>
                <label style={lbl} htmlFor={`rep-${f.code}`}>{f.libelle}</label>
                <Selecteur id={`rep-${f.code}`} value={reports[f.code] || ''} disabled={!peutParc}
                  onChange={(v) => setReports((p) => ({ ...p, [f.code]: v }))}>
                  <option value="">— suit la règle générale —</option>
                  {reg.politiques.map((p) => <option key={p.code} value={p.code}>{p.libelle}</option>)}
                </Selecteur>
              </div>
            ))}
          </div>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{reg.calendrier.fondement}</div>
      </section>

      {peutParc && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid var(--border-subtle)', paddingTop: 14 }}>
          <Bouton variante="primaire" taille="sm" onClick={enregistrer} disabled={busy === 'param'}>
            <I n="check" s={13} />Enregistrer le paramétrage
          </Bouton>
        </div>
      )}
      {!peutParc && (
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
          Seul le responsable qualité modifie ces réglages. Ils sont affichés parce que savoir quel
          délai vous est accordé fait partie du travail.
        </p>
      )}
    </div>
  );
}
